<template>
    <widget-card
        title="Lammie Requests & rumours"
        :loading="loading"
        :error="error"
        :empty="!loading && !payload"
        :hidden="hidden"
        @retry="fetchData"
    >
        <div class="rr-wrap" :class="{ 'is-dark': $vuetify.theme.dark }">
            <div v-if="payload" class="rr-section">
                <div class="rr-section-title">
                    <span>Lammies</span>
                    <span class="rr-section-count">({{ payload.lammies.total }})</span>
                </div>
                <div class="rr-row">
                    <div class="bar" :title="statusTooltip(payload.lammies)">
                        <div class="seg seg-edit" :style="{ width: pct(payload.lammies.editing, payload.lammies.total) + '%' }" />
                        <div class="seg seg-sub" :style="{ width: pct(payload.lammies.submitted, payload.lammies.total) + '%' }" />
                        <div class="seg seg-flag" :style="{ width: pct(payload.lammies.flagged, payload.lammies.total) + '%' }" />
                        <div class="seg seg-app" :style="{ width: pct(payload.lammies.approved, payload.lammies.total) + '%' }" />
                    </div>
                </div>
            </div>

            <div v-if="payload" class="rr-section">
                <div class="rr-section-title">
                    <span>Rumours</span>
                    <span class="rr-section-count">({{ payload.rumours.total }})</span>
                </div>
                <div v-for="t in payload.rumours.byType" :key="'t-' + t.type" class="rr-row rr-type-row">
                    <span class="rr-type">{{ t.type }}</span>
                    <div class="bar" :title="statusTooltip(t)">
                        <div class="seg seg-edit" :style="{ width: pct(t.editing, t.total) + '%' }" />
                        <div class="seg seg-sub" :style="{ width: pct(t.submitted, t.total) + '%' }" />
                        <div class="seg seg-flag" :style="{ width: pct(t.flagged, t.total) + '%' }" />
                        <div class="seg seg-app" :style="{ width: pct(t.approved, t.total) + '%' }" />
                    </div>
                    <span class="rr-count">{{ t.total }}</span>
                </div>
                <div class="rr-divider"></div>
                <div class="rr-row rr-type-row rr-total-row">
                    <span class="rr-type">Total</span>
                    <div class="bar" :title="statusTooltip(payload.rumours)">
                        <div class="seg seg-edit" :style="{ width: pct(payload.rumours.editing, payload.rumours.total) + '%' }" />
                        <div class="seg seg-sub" :style="{ width: pct(payload.rumours.submitted, payload.rumours.total) + '%' }" />
                        <div class="seg seg-flag" :style="{ width: pct(payload.rumours.flagged, payload.rumours.total) + '%' }" />
                        <div class="seg seg-app" :style="{ width: pct(payload.rumours.approved, payload.rumours.total) + '%' }" />
                    </div>
                    <span class="rr-count">{{ payload.rumours.total }}</span>
                </div>
            </div>

            <div v-if="payload" class="legend">
                <span><i class="swatch seg-edit"></i>Editing</span>
                <span><i class="swatch seg-sub"></i>Submitted</span>
                <span><i class="swatch seg-flag"></i>Flagged</span>
                <span><i class="swatch seg-app"></i>Approved</span>
            </div>
        </div>
    </widget-card>
</template>

<script>
import api from '@/api'
import WidgetCard from './WidgetCard.vue'

export default {
    name: 'RequestsRumoursWidget',
    components: { WidgetCard },
    props: {
        eventId: { type: Number, default: null },
        mode: { type: String, default: 'mine' },
        refreshKey: { type: Number, default: 0 },
    },
    data() {
        return { loading: false, error: null, payload: null, hidden: false, inFlightKey: null }
    },
    watch: {
        eventId: { handler: 'fetchData', immediate: true },
        mode: 'fetchData',
        refreshKey: 'fetchData',
    },
    methods: {
        endpointUrl() {
            const m = this.mode.charAt(0).toUpperCase() + this.mode.slice(1)
            return `dashboard/requestsRumours/${this.eventId}?mode=${m}`
        },
        async fetchData() {
            if (!this.eventId) { this.payload = null; return }
            const key = `${this.eventId}|${this.mode}`
            this.inFlightKey = key
            this.loading = true; this.error = null; this.hidden = false
            try {
                const data = await api.get(this.endpointUrl())
                if (this.inFlightKey !== key) return
                this.payload = data
            } catch (err) {
                if (this.inFlightKey !== key) return
                if (err.response && err.response.status === 403) { this.hidden = true; return }
                this.error = err
            } finally {
                if (this.inFlightKey === key) this.loading = false
            }
        },
        pct(part, total) {
            return total === 0 ? 0 : Math.round((part / total) * 100)
        },
        statusTooltip(row) {
            return [
                row.editing && `Editing ${row.editing}`,
                row.submitted && `Submitted ${row.submitted}`,
                row.flagged && `Flagged ${row.flagged}`,
                row.approved && `Approved ${row.approved}`,
            ].filter(Boolean).join(' · ')
        },
    },
}
</script>

<style scoped>
.rr-section { margin-bottom: 12px; }
.rr-section ~ .rr-section { margin-bottom: 0; }
.rr-section-title { font-weight: 600; font-size: 12px; margin-bottom: 4px; display: flex; gap: 4px; align-items: baseline; color: rgba(0,0,0,0.7); }
.rr-section-count { font-weight: 400; color: rgba(0,0,0,0.6); }
.rr-row { display: flex; align-items: center; gap: 8px; padding: 2px 0; font-size: 12px; }
.rr-type-row .rr-type { flex: 0 0 90px; }
.rr-row .bar { flex: 1; }
.rr-count { font-weight: 600; color: #1976d2; min-width: 28px; text-align: right; }
.rr-total-row .rr-type { font-weight: 600; }
.bar { display: flex; height: 12px; border-radius: 2px; overflow: hidden; background: #eee; }
.seg-edit { background: #1976d2; }
.seg-sub  { background: #ffa000; }
.seg-flag { background: #d32f2f; }
.seg-app  { background: #4caf50; }
.rr-divider { border-top: 1px dotted #ddd; margin: 4px 0; }
.legend { display: flex; gap: 12px; font-size: 11px; margin-top: 8px; color: rgba(0,0,0,0.7); }
.swatch { display: inline-block; width: 10px; height: 10px; border-radius: 2px; margin-right: 4px; vertical-align: middle; }
.rr-wrap.is-dark .rr-section-title { color: rgba(255,255,255,0.85); }
.rr-wrap.is-dark .rr-section-count { color: rgba(255,255,255,0.7); }
.rr-wrap.is-dark .rr-divider { border-top-color: rgba(255,255,255,0.18); }
.rr-wrap.is-dark .bar { background: rgba(255,255,255,0.12); }
.rr-wrap.is-dark .legend { color: rgba(255,255,255,0.85); }
</style>
